<template>
    <div class="w-full flex flex-wrap">
        <select-c
            v-model="modelPeriod"
            @change="handlePeriodChange"
            :options="periods"
            label="Period"
            wrapper-class="xs:w-full"
        />
        <select-c
            v-model="modelSort"
            @change="handleSortChange"
            :options="sorts"
            label="Sort By"
            wrapper-class="xs:w-full sm:ml-auto my-2 sm:my-0"
        />
        <!-- <div class="relative xs:w-full">
            <label for="selectPeriod" class="leading-none self-center text-black mr-2">Period:</label>
            <select
                v-model="modelPeriod"
                id="selectPeriod"
                class="form-control form-control-sm form-control-fixed bg-white mb-1"
                aria-label="Select Period"
            >
                <option v-for="option in periods" :class="{ 'bg-yellow-200': option.highlight }" :value="option.value">
                    {{ option.label }}
                </option>
            </select>
        </div>
        <div class="relative xs:w-full sm:ml-auto xs:mt-2">
            <label for="selectSortBy" class="mr-2">Sort By:</label>
            <select
                v-model="modelSort"
                id="selectSortBy"
                class="form-control form-control-sm form-control-fixed"
                aria-label="Select Sort"
            >
                <option v-for="sort in sorts" :class="{ 'bg-yellow-200': sort.highlight }" :value="sort.value">
                    {{ sort.label }}
                </option>
            </select>
        </div> -->
    </div>
</template>

<script setup>
import { ref, watch, computed, nextTick, onMounted, inject } from 'vue';
import { useLocationHash } from '../mixins/LocationHash';

const props = defineProps({
    periods: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits();
const eventBus = inject('eventBus');
const { hashExists, searchHash, addToHash } = useLocationHash();

const isInitialLoad = ref(true);

const defaultSort = { groupBy: 'uscCategory' };
const sortBySymbol = { groupBy: 'symbolName', orderBy: 'symbolName', orderDir: 'asc' };
const sorts = ref([
    { label: 'Category', hash: 'category', value: defaultSort, default: true },
    { label: 'Alphabetically', hash: 'alpha', value: sortBySymbol },
    { label: 'Low - Change', hash: 'lowPercent', value: 'low-Percent' },
    { label: 'Low - Date', hash: 'lowDate', value: 'low-Date' },
    { label: 'High - Change', hash: 'highPercent', value: 'high-Percent' },
    { label: 'High - Date', hash: 'highDate', value: 'high-Date' },
]);

const modelPeriod = ref(props.periods.find(mPeriod => mPeriod.default)?.value || (props.periods.length > 0 ? props.periods[0].value : ''));
const modelSort = ref(sorts.value.find(mSort => mSort.default)?.value || (sorts.value.length > 0 ? sorts.value[0].value : ''));

const isDynamicSort = computed(() => ['low-Percent', 'low-Date', 'high-Percent', 'high-Date'].includes(modelSort.value));
const dynamicSortBy = computed(() => {
    if (isDynamicSort.value) {
        const [lowHigh, field] = modelSort.value.split('-');
        const column = modelPeriod.value.columns.find(mColumn => mColumn.field.startsWith(lowHigh + field));
        return { orderBy: column.field, orderDir: 'desc' };
    }
});

// Emit request to DataTable
const emitRequest = () => {
    // Prevent emitting during the initial hash setup
    if (isInitialLoad.value) {
        return;
    }

    // Get the dynamic sort
    let selectedSort = modelSort.value;
    if (isDynamicSort.value) {
        selectedSort = dynamicSortBy.value;
    }

    // Prepare request and emit to DataTable
    const request = {
        ...modelPeriod.value.request,
        ...selectedSort
    };
    eventBus.emit('requestChanged', {
        request,
        labels: modelPeriod.value.labels,
        columns: modelPeriod.value.columns,
    });
};

// Toggle navigation to/from "category" search
function toggleNavigation() {
    if (modelSort.value.groupBy === 'uscCategory' || modelSort.value.groupBy === 'uscCategory') {
        eventBus.emit('toggle-navigation', 'show');
    } else {
        eventBus.emit('toggle-navigation', 'hide');
    }
}

// Handle selection of a new period
const handlePeriodChange = () => {
    // Find the label of the selected period
    const selectedPeriodLabel = props.periods.find(mPeriod => {
        return JSON.stringify(mPeriod.value) === JSON.stringify(modelPeriod.value);
    })?.label;

    addToHash('period', selectedPeriodLabel);
};

// Handle selection of a new sort
const handleSortChange = () => {
    // Find the label of the selected period
    const modelSortLabel = sorts.value.find(mSort => {
        return JSON.stringify(mSort.value) === JSON.stringify(modelSort.value);
    })?.hash;

    // Toggle coming to/from "category" search
    toggleNavigation();

    addToHash('sortBy', modelSortLabel);
};

// Set up a single watcher that checks both period and sort
watch([modelPeriod, modelSort], ([newPeriod, newSort], [oldPeriod, oldSort]) => {
    // This condition ensures we don't emit requests before the hash has been processed
    if (isInitialLoad.value) {
        return;
    }

    // Handle period change only if it's different from the old value
    if (newPeriod !== oldPeriod) {
        handlePeriodChange();
    }

    // Handle sort change only if it's different from the old value
    if (newSort !== oldSort) {
        handleSortChange();
    }

    // Only emit the request if there's an actual change
    if (newPeriod !== oldPeriod || newSort !== oldSort) {
        emitRequest();
    }
}, { immediate: false }); // Set immediate to false to prevent running on initial setup.

// Initialize the selections from hash
const initializeFromHash = async () => {
    const periodFromHash = searchHash('period');
    if (periodFromHash) {
        const foundPeriod = props.periods.find(mPeriod => mPeriod.label === periodFromHash.value);
        if (foundPeriod) {
            modelPeriod.value = foundPeriod.value;
        }
    }

    const sortFromHash = searchHash('sortBy');
    if (sortFromHash) {
        const foundSort = sorts.value.find(mSort => mSort.hash === sortFromHash.value);
        if (foundSort) {
            modelSort.value = foundSort.value;
            toggleNavigation();
        }
    }
};

// On mounted, handle the hash values and set up initial state
onMounted(async () => {
    if (hashExists()) {
        await initializeFromHash();
    }

    // Set the initial load flag to false now that we've processed the hash
    isInitialLoad.value = false;

    // Ensure the component is fully updated before emitting
    await nextTick();

    emitRequest();
});
</script>
